// Brand glyph registry.
//
// Most logos come straight from the `simple-icons` package (single-path 24x24,
// CC0 icon data). A few cloud/AI brands that simple-icons has dropped for
// trademark reasons (AWS, Azure, OpenAI) are inlined from pinned pre-removal
// releases in `brand-paths.ts`. Anything we don't have a real glyph for falls
// back to a deterministic, brand-coloured monogram tile so coverage is total
// and the tables stay visually consistent.

import type { SimpleIcon } from "simple-icons";
import {
  siPi, siOpencode, siClaudecode, siClaude, siCursor, siCline, siAmp, siGithubcopilot, siWarp, siZedindustries, siLmstudio,
  siAlibabacloud,
  siAnthropic,
  siCloudflare,
  siDeepseek,
  siGithub,
  siGooglecloud,
  siGooglegemini,
  siHetzner,
  siHuggingface,
  siKoyeb,
  siMeta,
  siMinimax,
  siMistralai,
  siModal,
  siMoonshotai,
  siNvidia,
  siOllama,
  siOpenrouter,
  siPoe,
  siQwen,
  siReplicate,
  siVercel,
  siXiaomi,
} from "simple-icons";
import { INLINE_BRANDS } from "./brand-paths";

export type BrandGlyph =
  | { kind: "asset"; title: string; hex: string; src: string }
  | { kind: "icon"; title: string; hex: string; path: string; viewBox?: string }
  | { kind: "mono"; title: string; hex: string; label: string };

const fromSi = (si: SimpleIcon): BrandGlyph => ({
  kind: "icon",
  title: si.title,
  hex: `#${si.hex}`,
  path: si.path,
});

const fromInline = (key: keyof typeof INLINE_BRANDS): BrandGlyph => {
  const brand = INLINE_BRANDS[key];
  return { kind: "icon", title: brand.title, hex: brand.hex, path: brand.path, viewBox: "viewBox" in brand ? brand.viewBox : undefined };
};

const mono = (title: string, label: string, hex: string): BrandGlyph => ({
  kind: "mono",
  title,
  hex,
  label,
});

// Canonical, fully-resolved brands keyed by a normalized slug.
const REGISTRY: Record<string, BrandGlyph> = {
  // Cloud / object storage
  aws: fromInline("aws"),
  amazon: fromInline("aws"),
  azure: fromInline("azure"),
  cloudflare: fromSi(siCloudflare),
  gcp: fromSi(siGooglecloud),
  googlecloud: fromSi(siGooglecloud),
  hetzner: fromSi(siHetzner),

  // AI labs
  openai: fromInline("openai"),
  anthropic: fromSi(siAnthropic),
  google: fromSi(siGooglegemini),
  googlegemini: fromSi(siGooglegemini),
  deepseek: fromSi(siDeepseek),
  meta: fromSi(siMeta),
  metallama: fromSi(siMeta),
  minimax: fromSi(siMinimax),
  mistralai: fromSi(siMistralai),
  mistral: fromSi(siMistralai),
  moonshotai: fromSi(siMoonshotai),
  moonshot: fromSi(siMoonshotai),
  nvidia: fromSi(siNvidia),
  qwen: fromSi(siQwen),
  alibaba: fromSi(siAlibabacloud),
  xiaomi: fromSi(siXiaomi),
  ollama: fromSi(siOllama),
  nousresearch: mono("Nous Research", "N", "#10A37F"),
  rekaai: mono("Reka AI", "R", "#6D28D9"),
  submodel: mono("SubModel", "S", "#2563EB"),
  upstage: mono("Upstage", "U", "#7C3AED"),
  zai: fromInline("zai"),
  xai: fromInline("xai"),
  opencode: fromSi(siOpencode), t3:fromInline("t3"), omp:fromInline("omp"), "pi-agent":fromSi(siPi),
  // Official NousResearch/hermes-agent website/static/img/favicon.svg.
  hermes: {kind:"asset",title:"Hermes Agent",hex:"#000000",src:"/brand-icons/hermes.svg"},
  claudecode: fromSi(siClaudecode), claude: fromSi(siClaude), cursor:fromSi(siCursor), cline:fromSi(siCline), amp:fromSi(siAmp), copilot:fromSi(siGithubcopilot), warp:fromSi(siWarp), zed:fromSi(siZedindustries), lmstudio:fromSi(siLmstudio),

  // Serverless GPU hosts + app platforms
  modal: fromSi(siModal),
  koyeb: fromSi(siKoyeb),
  huggingface: fromSi(siHuggingface),
  replicate: fromSi(siReplicate),
  runpod: mono("RunPod", "RP", "#673AB7"),
  beam: mono("Beam", "Be", "#6366F1"),
  cerebrium: mono("Cerebrium", "Ce", "#22C55E"),
  nebius: mono("Nebius", "Ne", "#1E40AF"),
  novita: mono("Novita", "No", "#7C3AED"),
  saladcloud: mono("SaladCloud", "Sa", "#16A34A"),
  verda: mono("Verda", "Ve", "#0EA5E9"),

  // Gateways / aggregators
  github: fromSi(siGithub),
  vercel: fromSi(siVercel),
  poe: fromSi(siPoe),
  openrouter: fromSi(siOpenrouter),
  groq: mono("Groq", "Gq", "#F55036"),
  siliconflow: mono("SiliconFlow", "Sf", "#7C3AED"),
  bergetai: mono("Berget.AI", "Be", "#0EA5E9"),
  gmicloud: mono("GMI Cloud", "G", "#2563EB"),
  ionet: mono("IO.NET", "io", "#10B981"),
  kilogateway: mono("Kilo Gateway", "K", "#EA580C"),
  nanogpt: mono("NanoGPT", "Ng", "#0891B2"),
  zenmux: mono("ZenMux", "Zm", "#9333EA"),
};

// Ordered substring matches for noisy display names. First match wins, so more
// specific keys (e.g. googlecloud) must come before broader ones (e.g. google).
const ALIASES: Array<[needle: string, glyph: BrandGlyph]> = [
  ["cloudflare", REGISTRY.cloudflare],
  ["hetzner", REGISTRY.hetzner],
  ["googlecloud", REGISTRY.googlecloud],
  ["googlegemini", REGISTRY.googlegemini],
  ["google", REGISTRY.google],
  ["huggingface", REGISTRY.huggingface],
  ["amazon", REGISTRY.aws],
  ["aws", REGISTRY.aws],
  ["azure", REGISTRY.azure],
  ["openrouter", REGISTRY.openrouter],
  ["openai", REGISTRY.openai],
  ["chatgpt", REGISTRY.openai],
  ["gpt", REGISTRY.openai],
  ["anthropic", REGISTRY.anthropic],
  ["claude", REGISTRY.anthropic],
  ["gemini", REGISTRY.google],
  ["grok", REGISTRY.xai],
  ["xai", REGISTRY.xai],
  ["metallama", REGISTRY.meta],
  ["llama", REGISTRY.meta],
  ["meta", REGISTRY.meta],
  ["mixtral", REGISTRY.mistralai],
  ["mistral", REGISTRY.mistralai],
  ["moonshot", REGISTRY.moonshotai],
  ["zai", REGISTRY.zai],
  ["minimax", REGISTRY.minimax],
  ["deepseek", REGISTRY.deepseek],
  ["nvidia", REGISTRY.nvidia],
  ["qwen", REGISTRY.qwen],
  ["nous", REGISTRY.nousresearch],
  ["reka", REGISTRY.rekaai],
  ["vercel", REGISTRY.vercel],
  ["github", REGISTRY.github],
  ["novita", REGISTRY.novita],
  ["runpod", REGISTRY.runpod],
  ["salad", REGISTRY.saladcloud],
  ["nebius", REGISTRY.nebius],
  ["cerebrium", REGISTRY.cerebrium],
  ["beam", REGISTRY.beam],
  ["verda", REGISTRY.verda],
  ["modal", REGISTRY.modal],
  ["koyeb", REGISTRY.koyeb],
  ["replicate", REGISTRY.replicate],
  ["groq", REGISTRY.groq],
  ["siliconflow", REGISTRY.siliconflow],
  ["berget", REGISTRY.bergetai],
  ["poe", REGISTRY.poe],
];

const MONO_PALETTE = [
  "#6366F1",
  "#0EA5E9",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#14B8A6",
  "#EF4444",
];

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

// Split a name into word tokens, breaking on delimiters, camelCase, and
// letter/digit seams ("gpt-5-chat" -> ["gpt","5","chat"], "MiniMaxAI" ->
// ["mini","max","ai"]). Used so short aliases only match whole words.
const tokenize = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])([0-9])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const monogramLabel = (name: string) => {
  const words = name
    .replace(/[._/-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const single = words[0] ?? name;
  return single.slice(0, 2).replace(/^./, (c) => c.toUpperCase());
};

const monogramColor = (name: string) => {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }
  return MONO_PALETTE[hash % MONO_PALETTE.length];
};

const fallbackCache = new Map<string, BrandGlyph>();

const fallbackMonogram = (name: string): BrandGlyph => {
  const cached = fallbackCache.get(name);
  if (cached) return cached;
  const glyph = mono(name || "Unknown", monogramLabel(name || "?"), monogramColor(name || "?"));
  fallbackCache.set(name, glyph);
  return glyph;
};

export const resolveBrand = (raw: string | null | undefined): BrandGlyph => {
  const name = (raw ?? "").trim();
  if (!name) return fallbackMonogram("Unknown");
  const key = normalize(name);
  if (REGISTRY[key]) return REGISTRY[key];
  const tokens = tokenize(name);
  for (const [needle, glyph] of ALIASES) {
    // Short needles (gpt, poe, xai) only match a whole word to avoid
    // false positives like "ftpo-exp501" -> Poe or "MiniMaxAI" -> xAI.
    const matched = needle.length <= 3 ? tokens.includes(needle) : key.includes(needle);
    if (matched) return glyph;
  }
  return fallbackMonogram(name);
};

// Pull the owning lab out of a model id like "anthropic/claude-3.7-sonnet".
// A "/" prefix is the owner. Otherwise resolve the bare model name first (so
// "gemini-2.5-pro" served via a gateway still shows Google), and only fall back
// to the provider/gateway when the model name itself isn't a recognised brand.
export const brandFromModelName = (modelName: string, provider?: string): BrandGlyph => {
  if (modelName.includes("/")) return resolveBrand(modelName.split("/")[0]);
  const fromModel = resolveBrand(modelName);
  if (fromModel.kind === "icon") return fromModel;
  return provider ? resolveBrand(provider) : fromModel;
};

// Relative luminance (sRGB). Used to decide whether a brand colour is dark
// enough that it would vanish against the dark UI and should inherit instead.
export const isDarkHex = (hex: string) => {
  const clean = hex.replace("#", "");
  if (clean.length < 6) return false;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const channel = (value: number) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance < 0.2;
};
